/**
 * Glimmery offer state, see snippets/g-offer.liquid and docs/OFFERS.md.
 *
 * 1. State: asks Shopify (Storefront API, tokenless) through throwaway carts that are never shown or checked out:
 *    - giveaway: 1 tee + giveaway codes + a US address → is the tee free AND is shipping free?  → state A
 *    - launch:   2 tees + launch code + a US address  → does the launch code take money off?   → state B
 *      (the same cart tells whether "free shipping on 2+ tees" really applies → html.g-ship2)
 *    - neither                                                                                  → state C
 *    The answer is kept for 2 minutes in sessionStorage (this tab only). No answer → no promo class at all.
 * 2. Bag: the visitor's cart carries exactly the codes of the current state (other codes stay). If a code the bag
 *    had is gone because the offer was used up, the bag says so (html.g-notice-claimed / g-notice-ended) instead
 *    of silently showing a higher total.
 * 3. Checkout: every checkout submit re-asks Shopify first. Offer changed → bag updated, notice shown, no redirect;
 *    the shopper reviews the new total and presses Checkout again.
 * 4. Shipping in the bag: the real bag (lines + codes) is priced once more with a US address; only if shipping
 *    then costs nothing does html.g-cart-ship-free reveal "Free standard US shipping included."
 */
(() => {
  const cfg = window.GlimmeryOffer;
  if (!cfg || !cfg.variant) return;

  const root = document.documentElement;
  const STATE_KEY = 'g-offer:state';
  const NOTICE_KEY = 'g-offer:notice';
  const TTL = 2 * 60 * 1000;
  const US_ADDRESS = { address1: '350 5th Ave', city: 'New York', provinceCode: 'NY', zip: '10118', countryCode: 'US' };
  const up = (code) => String(code || '').toUpperCase();
  const giveCodes = cfg.giveaway ? [cfg.giveaway.code, cfg.giveaway.shipping] : [];
  const launchCodes = cfg.launch ? [cfg.launch.code] : [];
  const ours = new Set([...giveCodes, ...launchCodes].map(up));
  const configKey = JSON.stringify([cfg.giveaway, cfg.launch]);
  const isUS = cfg.country === 'US';
  const cartUrl = (window.routes && window.routes.cart_url) || '/cart';
  const cartUpdateUrl = (window.routes && window.routes.cart_update_url) || '/cart/update';

  const QUERY = `mutation GlimmeryOfferCheck($lines: [CartLineInput!], $codes: [String!], $country: CountryCode,
    $addresses: [CartSelectableAddressInput!]) @inContext(country: $country) {
    cartCreate(input: { lines: $lines, discountCodes: $codes, buyerIdentity: { countryCode: $country },
      delivery: { addresses: $addresses } }) {
      cart {
        cost { totalAmount { amount } }
        discountCodes { code applicable }
        discountAllocations { discountedAmount { amount } discountApplication { targetType }
          ... on CartCodeDiscountAllocation { code } }
        lines(first: 50) { nodes { cost { subtotalAmount { amount } }
          discountAllocations { discountedAmount { amount } discountApplication { targetType }
            ... on CartCodeDiscountAllocation { code } } } }
        deliveryGroups(first: 5) { nodes { selectedDeliveryOption { estimatedCost { amount } } } }
      }
    }
  }`;

  // Prices a throwaway cart. Returns null when Shopify gives no usable answer.
  async function simulate(lines, codes, country, withAddress) {
    const response = await fetch(`/api/${cfg.apiVersion}/graphql.json`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        query: QUERY,
        variables: {
          lines,
          codes,
          country: country || null,
          addresses: withAddress ? [{ selected: true, address: { deliveryAddress: US_ADDRESS } }] : [],
        },
      }),
    });
    if (!response.ok) return null;
    const cart = (await response.json())?.data?.cartCreate?.cart;
    if (!cart) return null;

    const num = (money) => parseFloat(money?.amount || '0') || 0;
    const applicable = {};
    cart.discountCodes.forEach((item) => (applicable[up(item.code)] = item.applicable));
    const byCode = {};
    let shipDiscount = 0;
    const allocations = [...cart.discountAllocations, ...cart.lines.nodes.flatMap((line) => line.discountAllocations)];
    allocations.forEach((item) => {
      const amount = num(item.discountedAmount);
      if (item.discountApplication?.targetType === 'SHIPPING_LINE') shipDiscount += amount;
      else if (item.code) byCode[up(item.code)] = (byCode[up(item.code)] || 0) + amount;
    });
    const groups = cart.deliveryGroups.nodes;
    const shipCost = groups.reduce((sum, group) => sum + num(group.selectedDeliveryOption?.estimatedCost), 0);
    return {
      applicable,
      byCode,
      items: cart.lines.nodes.reduce((sum, line) => sum + num(line.cost.subtotalAmount), 0),
      shipKnown: withAddress && groups.length > 0 && groups.every((group) => group.selectedDeliveryOption),
      shipCost,
      shipFree: shipCost > 0 && shipDiscount >= shipCost - 0.005,
      total: num(cart.cost.totalAmount),
    };
  }

  const tee = (quantity) => [{ merchandiseId: `gid://shopify/ProductVariant/${cfg.variant}`, quantity }];

  async function askShopify() {
    let giveaway = false;
    let shipOne = 0;
    if (cfg.giveaway) {
      const result = await simulate(tee(1), giveCodes, 'US', true);
      if (!result) return null;
      if (result.shipKnown) shipOne = result.shipCost;
      giveaway =
        result.applicable[up(cfg.giveaway.code)] === true &&
        (result.byCode[up(cfg.giveaway.code)] || 0) >= result.items - 0.005 &&
        result.shipKnown &&
        result.shipFree;
    }
    let launch = false;
    let ship2 = false;
    if (!giveaway || !isUS) {
      const result = await simulate(tee(2), launchCodes, 'US', true);
      if (!result) return null;
      launch = Boolean(cfg.launch) && result.applicable[up(cfg.launch.code)] === true && (result.byCode[up(cfg.launch.code)] || 0) > 0;
      ship2 = Boolean(cfg.shipping) && result.shipKnown && result.shipFree;
    }
    // Standard US shipping for one tee (product page line "Standard US shipping: $5.00 for one tee.").
    if (isUS && !shipOne && !giveaway) {
      const result = await simulate(tee(1), [], 'US', true);
      if (result && result.shipKnown) shipOne = result.shipCost;
    }
    const state = giveaway ? 'a' : launch ? 'b' : 'c';
    // The giveaway includes US shipping and is limited to the US market: other visitors see the regular shop.
    const view = state === 'a' && !isUS ? 'c' : state;
    return { state, view, ship2: view !== 'a' && ship2, shipOne, t: Date.now(), key: configKey };
  }

  function readState() {
    try {
      const saved = JSON.parse(sessionStorage.getItem(STATE_KEY));
      if (saved && saved.key === configKey && Date.now() - saved.t < TTL) return saved;
    } catch (e) {}
    return null;
  }

  let pending = null;
  // fresh = true skips the 2-minute memory (checkout, stale bag codes).
  function resolveState(fresh) {
    if (!fresh) {
      const saved = readState();
      if (saved) return Promise.resolve(saved);
    }
    if (pending) return pending;
    pending = askShopify()
      .catch(() => null)
      .then((result) => {
        pending = null;
        if (result) {
          try {
            sessionStorage.setItem(STATE_KEY, JSON.stringify(result));
          } catch (e) {}
        }
        return result;
      });
    return pending;
  }

  let current = null;
  function apply(result) {
    current = result;
    root.classList.remove('g-offer-a', 'g-offer-b', 'g-offer-c', 'g-ship2');
    fillShipOne();
    if (!result) return;
    root.classList.add(`g-offer-${result.view}`);
    if (result.ship2) root.classList.add('g-ship2');
    document.dispatchEvent(new CustomEvent('glimmery:offer', { detail: result }));
  }

  // "Standard US shipping: $5.00 for one tee." Only with a real quote, never during the giveaway (shipping is free
  // then). Product sections are re-rendered on Front/Back swaps, so new copies are filled as they appear.
  function fillShipOne() {
    const ok = current && isUS && current.view !== 'a' && current.shipOne > 0;
    const text = ok ? new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(current.shipOne) : '';
    document.querySelectorAll('[data-g-ship-one]').forEach((item) => {
      const slot = item.querySelector('[data-g-ship-one-cost]');
      if (slot && slot.textContent !== text) slot.textContent = text;
      item.hidden = !ok;
    });
  }
  let fillQueued = false;
  new MutationObserver(() => {
    if (fillQueued) return;
    fillQueued = true;
    requestAnimationFrame(() => {
      fillQueued = false;
      if (document.querySelector('[data-g-ship-one]')) fillShipOne();
    });
  }).observe(document.documentElement, { childList: true, subtree: true });

  // ---------- notices ----------
  function showNotice(kind) {
    root.classList.toggle('g-notice-claimed', kind === 'claimed');
    root.classList.toggle('g-notice-ended', kind === 'ended');
    try {
      if (kind) sessionStorage.setItem(NOTICE_KEY, kind);
      else sessionStorage.removeItem(NOTICE_KEY);
    } catch (e) {}
  }
  try {
    const kind = sessionStorage.getItem(NOTICE_KEY);
    if (kind) showNotice(kind);
  } catch (e) {}

  // ---------- bag ----------
  const getCart = () => fetch(`${cartUrl}.js`, { headers: { Accept: 'application/json' } }).then((r) => r.json());

  const refreshViews = () => {
    if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      publish(PUB_SUB_EVENTS.cartUpdate, { source: 'g-offer' });
    }
  };

  // Puts exactly the current state's codes on the bag. Returns 'claimed' / 'ended' when a promise the bag carried
  // is no longer available, otherwise null.
  async function syncCart(result) {
    if (cfg.designMode || !result) return null;
    const cart = await getCart();
    const current = (cart.discount_codes || []).map((item) => item.code);
    const others = current.filter((code) => !ours.has(up(code)));
    const want = result.view === 'a' ? giveCodes : result.view === 'b' ? launchCodes : [];
    const next = [...others, ...want];
    const same = next.length === current.length && next.every((code) => current.some((c) => up(c) === up(code)));
    if (same) return null;

    const had = (code) => code && current.some((c) => up(c) === up(code));
    let lost = null;
    if (cart.item_count > 0) {
      if (cfg.giveaway && had(cfg.giveaway.code) && result.state !== 'a') lost = 'claimed';
      else if (cfg.launch && had(cfg.launch.code) && result.state === 'c') lost = 'ended';
    }
    await fetch(`${cartUpdateUrl}.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ discount: next.join(',') }),
    });
    if (lost) showNotice(lost);
    if (cart.item_count > 0) refreshViews();
    return lost;
  }

  // Real bag, priced with a US address: confirms (or not) that shipping is free for exactly these lines and codes.
  let verifySeq = 0;
  async function verifyBagShipping(cart) {
    const seq = ++verifySeq;
    const lines = (cart.items || []).map((item) => ({
      merchandiseId: `gid://shopify/ProductVariant/${item.variant_id}`,
      quantity: item.quantity,
    }));
    if (!lines.length || !isUS) {
      root.classList.remove('g-cart-ship-free');
      return;
    }
    const codes = (cart.discount_codes || []).map((item) => item.code);
    const result = await simulate(lines, codes, cfg.country, true).catch(() => null);
    if (seq !== verifySeq) return;
    root.classList.toggle('g-cart-ship-free', Boolean(result && result.shipKnown && result.shipFree));
  }

  async function checkBag(freshIfStale) {
    if (cfg.designMode) return;
    try {
      let cart = await getCart();
      if (!cart.item_count) {
        showNotice(null);
        root.classList.remove('g-cart-ship-free');
        return;
      }
      const mainCodes = [cfg.giveaway?.code, cfg.launch?.code].filter(Boolean).map(up);
      const stale = (cart.discount_codes || []).some((item) => mainCodes.includes(up(item.code)) && item.applicable === false);
      if (stale && freshIfStale) {
        const result = await resolveState(true);
        apply(result);
        if (await syncCart(result)) cart = await getCart();
      }
      await verifyBagShipping(cart);
    } catch (e) {}
  }

  // ---------- checkout ----------
  function isCheckoutSubmit(event) {
    const submitter = event.submitter;
    if (submitter) return submitter.name === 'checkout';
    return Boolean(event.target.querySelector && event.target.querySelector('[name="checkout"]'));
  }

  const withTimeout = (promise, ms) =>
    Promise.race([promise, new Promise((resolve) => setTimeout(() => resolve(null), ms))]);

  document.addEventListener(
    'submit',
    async (event) => {
      const form = event.target;
      if (cfg.designMode || !(form instanceof HTMLFormElement) || !isCheckoutSubmit(event)) return;
      if (form.dataset.gOfferChecked === '1') {
        delete form.dataset.gOfferChecked;
        showNotice(null);
        return;
      }
      event.preventDefault();
      event.stopPropagation();
      const submitter = event.submitter;
      if (submitter) submitter.setAttribute('aria-busy', 'true');
      let lost = null;
      try {
        const result = await withTimeout(resolveState(true), 6000);
        if (result) {
          apply(result);
          lost = await syncCart(result);
        }
      } catch (e) {}
      if (submitter) submitter.removeAttribute('aria-busy');
      if (lost) {
        const notice = document.querySelector(`[data-g-notice="${lost}"]`);
        if (notice) notice.scrollIntoView({ block: 'nearest' });
        return;
      }
      form.dataset.gOfferChecked = '1';
      if (submitter && submitter.form === form) form.requestSubmit(submitter);
      else form.requestSubmit();
    },
    true
  );

  // ---------- start ----------
  async function run() {
    const result = await resolveState(false);
    apply(result);
    try {
      await syncCart(result);
    } catch (e) {}
    checkBag(true);
  }

  document.addEventListener('DOMContentLoaded', () => {
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      let timer;
      subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
        clearTimeout(timer);
        timer = setTimeout(() => checkBag(event?.source !== 'g-offer'), 400);
      });
    }
  });

  run();
})();
